import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { set } from "lodash";

export interface VendedorType {
    id: number
    name: string;
    idEmpresa?: number;
}

export interface CreateVendedorType {
    name: string;
    empresaId: number;
}

export interface EditVendedorType {
    id: number;
    name: string;
}

export default function useVendedor() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listVendedor, setListVendedor] = useState<VendedorType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListVendedor, setInitialListVendedor] = useState<VendedorType[]>([]);
    const [vendedor, setVendedor] = useState<VendedorType>({
        id: 0,
        name: "",
        idEmpresa: objUserTk.EmpresaId,
    });

    function createVendedor(createVendedorType: CreateVendedorType): Promise<CreateVendedorType | null> {
        setLoading(true);
        return api
            .post(`/Vendedor/create`, createVendedorType)
            .then((response) => {
                const createdVendedor: CreateVendedorType = response.data;
                showToast("success", "Vendedor criado com sucesso.");
                setLoading(false);
                return createdVendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar vendedor. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar vendedor:", error);
                return null;
            });
    }

    function getVendedorPaged(
        pageNumber: number,
        Name?: string,
        EmpresaId?: number,
        pageSize?: number
    ): Promise<VendedorType[] | null> {
        setLoading(true);
        let url = `/Vendedor/getVendedorPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (Name) {
            url += `&Name=${Name}`;
        }

        if (EmpresaId) {
            url += `&EmpresaId=${EmpresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListVendedor(response.data.result);
                setInitialListVendedor(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar vendedores. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar vendedores:", error);
                return null;
            });
    }

    function updateVendedor(updateVendedorData: EditVendedorType): Promise<EditVendedorType | null> {
        setLoading(true);
        return api
            .put(`/Vendedor/${updateVendedorData.id}`, updateVendedorData)
            .then((response) => {
                showToast("success", "Vendedor atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar vendedor. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar vendedor:", error);
                return null;
            });
    }

    function deleteVendedor(vendedorId: number): Promise<VendedorType | null> {
        setLoading(true);
        return api
            .delete(`/Vendedor/delete/${vendedorId}`)
            .then(() => {
                setVendedor({
                    id: 0,
                    name: "",
                });
                showToast("success", "Vendedor deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar vendedor. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar vendedor:", error);
                return null;
            });
    }

    function handleGetVendedorByEmpresaId(empresaId: number): Promise<VendedorType[] | null> {
        setLoading(true);
        return api
            .get(`/Vendedor/getVendedorByEmpresaId/${empresaId}`)
            .then((response) => {
                const vendedor: VendedorType[] = response.data;
                setListVendedor(vendedor);
                setLoading(false);
                return vendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações do vendedor. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações do vendedor:", error);
                return null;
            });
    }
    
    function handleGetVendedorById(vendedorId: number): Promise<VendedorType | null> {
        setLoading(true);
        return api
            .get(`/Vendedor/${vendedorId}`)
            .then((response) => {
                const vendedor: VendedorType = response.data;
                setVendedor(vendedor);
                setLoading(false);
                return vendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar vendedor pelo ID. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar vendedor pelo ID:", error);
                return null;
            });
    }    

    return {
        createVendedor,
        getVendedorPaged,
        updateVendedor,
        deleteVendedor,
        handleGetVendedorByEmpresaId,
        handleGetVendedorById,
        setListVendedor,
        listVendedor,
        setPageCount,
        pageCount,
        setInitialListVendedor,
        initialListVendedor,
        setVendedor,
        vendedor,
    };
}